/* Iter 103.0 Phase 2 · Menu Search Helpers
   ─────────────────────────────────────────
   Pure, deterministic, side-effect-free helpers for the Global
   Search dialog's "Screens & Menus" result group. Consumed by
   `GlobalSearchBar.jsx`.

   Design:
     • Zero React — trivially unit-testable.
     • Zero backend calls — menu index lives in memory (~80-120
       entries after RBAC filter).
     • RBAC uses the same `isItemVisible()` gate as the sidebar
       (no new authorization logic; REEDOS decision #2).
     • Scoring:
         Band 1 (100)  Exact label match (case-insensitive)
         Band 2 ( 80)  Label startsWith query
         Band 3 ( 60)  Label contains query
         Band 4 ( 40)  Keyword synonym contains query
         Band 5 ( 20)  Breadcrumb path contains query
       Tie-break: shorter label wins (more specific), then A-Z.
       Hidden orphans deprioritised within their score band (-5).
     • Cap: default 8 hits per query (matches business-record
       PER_ENTITY_LIMIT convention on the backend).

   Future-proofing (REEDOS decision #10 — Universal Command Palette):
     Item shape emitted by `buildMenuIndex()` carries a `kind: "nav"`
     tag. Adding future kinds ("action" | "recent" | "favourite")
     requires no signature change and no re-architecture.
*/

import { flattenNavItems } from "./navRegistry";
import SEARCH_KEYWORDS from "./searchKeywords";

/**
 * Build the searchable menu index for the CURRENT user.
 *
 * Memoise the CALLER SIDE — key on `user.id` + a stable hash of the
 * permission set. The auth context already ensures permissions are
 * immutable per session, so a single memo per (user, permissions)
 * pair is sufficient.
 *
 * @param {Object} user      auth user object (may be null pre-login)
 * @param {Function} can     permission check (perm => boolean)
 * @returns {Array<Object>}  Flat array of visible menu items with
 *                           `keywords` merged in from SEARCH_KEYWORDS.
 */
export function buildMenuIndex(user, can) {
    const flat = flattenNavItems(user, can);
    return flat.map((item) => ({
        ...item,
        keywords: SEARCH_KEYWORDS[item.href] || [],
    }));
}

/**
 * Filter + score + rank menu items against a query string.
 *
 * @param {Array<Object>} index  Output of buildMenuIndex()
 * @param {string} query         Raw query (untrimmed; we trim inline)
 * @param {number} limit         Max hits to return (default 8)
 * @returns {Array<Object>}      Ranked list of hits, each item is a
 *                               shallow copy of the index entry.
 */
export function filterMenuHits(index, query, limit = 8) {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return [];

    const scored = [];
    for (const item of index) {
        const label = String(item.label || "").toLowerCase();
        const breadcrumb = (item.breadcrumb || []).join(" > ").toLowerCase();
        const keywords = (item.keywords || []).map((k) => String(k).toLowerCase());

        let score = 0;
        if (label === q)                                                 score = 100;
        else if (label.startsWith(q))                                    score = 80;
        else if (label.includes(q))                                      score = 60;
        else if (keywords.some((k) => k === q || k.includes(q)))         score = 40;
        else if (breadcrumb.includes(q))                                 score = 20;

        if (score === 0) continue;
        // Hidden orphans deprioritised within their band. A `sidebar:false`
        // route is a legitimate destination but discoverability signal is
        // weaker than a first-class sidebar item.
        if (item.hidden) score -= 5;
        scored.push({ item, score });
    }

    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Tie-break 1: shorter label wins (more specific match).
        const aLen = String(a.item.label || "").length;
        const bLen = String(b.item.label || "").length;
        if (aLen !== bLen) return aLen - bLen;
        // Tie-break 2: alphabetical.
        return String(a.item.label || "").localeCompare(String(b.item.label || ""));
    });

    return scored.slice(0, limit).map((s) => s.item);
}
